import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildChordSyncState, buildTimelineStarts, normalizeChordChanges } from "@/music/chords/chordSyncUtils";

const UPDATE_INTERVAL_MS = 90;

const initialSnapshot = {
  currentTime: 0,
  duration: 0,
  currentIndex: -1,
  currentChord: null,
  nextChord: null,
  timeToNextChord: null,
};

const usePracticeChordSync = (playerRef, chordTimeline = [], options = {}) => {
  const { duration: durationHint = 0, enabled = true } = options;
  const chords = useMemo(() => normalizeChordChanges(chordTimeline, durationHint), [chordTimeline, durationHint]);
  const starts = useMemo(() => buildTimelineStarts(chords), [chords]);
  const [snapshot, setSnapshot] = useState({ ...initialSnapshot, duration: Number(durationHint) || 0 });
  const [isPlaying, setIsPlaying] = useState(false);
  const lastUiUpdateRef = useRef(0);
  const lastChordIndexRef = useRef(-1);

  const syncNow = useCallback(
    (force = false) => {
      if (!enabled) return;
      const player = playerRef.current;
      if (!player?.getCurrentTime) return;
      const currentTime = Math.max(0, Number(player.getCurrentTime()) || 0);
      const playerDuration = Number(player.getDuration?.()) || Number(durationHint) || 0;
      const chordSync = buildChordSyncState(chords, starts, currentTime, playerDuration);
      const now = performance.now();
      const chordChanged = chordSync.activeIndex !== lastChordIndexRef.current;
      if (!force && !chordChanged && now - lastUiUpdateRef.current < UPDATE_INTERVAL_MS) return;
      lastChordIndexRef.current = chordSync.activeIndex;
      lastUiUpdateRef.current = now;
      setSnapshot({
        currentTime,
        duration: playerDuration,
        currentIndex: chordSync.activeIndex,
        currentChord: chordSync.activeChord,
        nextChord: chordSync.nextChord,
        timeToNextChord: chordSync.timeToNextChord,
      });
    },
    [chords, durationHint, enabled, playerRef, starts]
  );

  const handlePlayerStateChange = useCallback(
    (state) => {
      const playing = state === 1 || state === "playing";
      setIsPlaying(playing);
      syncNow(true);
    },
    [syncNow]
  );

  useEffect(() => {
    if (!enabled) return undefined;
    syncNow(true);
    if (!isPlaying) return undefined;
    let frameId;
    const tick = () => {
      syncNow(false);
      frameId = window.requestAnimationFrame(tick);
    };
    frameId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frameId);
  }, [enabled, isPlaying, syncNow]);

  const seekTo = useCallback(
    (seconds) => {
      const safeTime = Math.max(0, Number(seconds) || 0);
      playerRef.current?.seekTo?.(safeTime);
      window.requestAnimationFrame(() => syncNow(true));
    },
    [playerRef, syncNow]
  );

  const progress = snapshot.duration > 0 ? Math.min(100, (snapshot.currentTime / snapshot.duration) * 100) : 0;

  return {
    ...snapshot,
    chords,
    isPlaying,
    progress,
    seekTo,
    syncNow,
    handlePlayerStateChange,
  };
};

export default usePracticeChordSync;
